import { Link } from '@tanstack/react-router';
import { type PaginationState, type SortingState } from '@tanstack/react-table';
import { format } from 'date-fns';
import { CircleArrowLeft, Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { toast } from 'sonner';

import {
  reportingAttendanceReportAPI,
  useAttendanceReport,
} from '~entities/student/api/reporting-attendance-report';

import { DateRangePicker } from '~features/payment/ui/date-range-picker';

import { KEYS } from '~shared/api/query-keys';
import { DataTable } from '~shared/components/table';
import { DataTablePagination } from '~shared/components/table/data-table-pagination';
import { Button } from '~shared/shadcn/ui/button';

import { attendanceReportColumns } from './attendance-report.columns';
import {
  defaultAbsenceDateRange,
  toAttendanceReportRangeParams,
} from './attendance-report.helpers';

function downloadCsvFile(csvText: string, filename: string) {
  const blob = new Blob([csvText], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function AttendanceReportPage() {
  const [absenceDateRange, setAbsenceDateRange] = useState<
    DateRange | undefined
  >(defaultAbsenceDateRange);
  const rangeParams = toAttendanceReportRangeParams(absenceDateRange);
  const { data, isFetching, isError, refetch } =
    useAttendanceReport(rangeParams);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'lastAttendanceDate', desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const rows = data?.rows ?? [];
  const studioName = data?.studioName.trim() ?? '';

  const handleDownloadCsv = async () => {
    try {
      setIsDownloading(true);
      const csvText = await reportingAttendanceReportAPI(
        KEYS.EXPORT_FORMAT.CSV,
        rangeParams,
      );
      downloadCsvFile(
        csvText,
        `attendance-report-${format(new Date(), 'yyyy-MM-dd')}.csv`,
      );
      toast.success('Attendance report downloaded.');
    } catch (error) {
      console.error('Failed to download the attendance report', { error });
      toast.error(
        'We could not download the attendance report. Please try again.',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className='space-y-6 p-4 sm:p-6'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
        <div>
          <Link
            to='/payment/payment-reports'
            className='mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground'
          >
            <CircleArrowLeft className='size-4' />
            Back to Reports
          </Link>
          <h1 className='text-xl font-bold text-foreground sm:text-2xl'>
            Attendance Report
          </h1>
          <p className='mt-1 text-sm text-muted-foreground'>
            {studioName
              ? `${studioName} — ${rows.length} students`
              : 'Lifetime and recent attendance for each student'}
          </p>
        </div>
        <div className='flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center'>
          <DateRangePicker
            date={absenceDateRange}
            setDate={setAbsenceDateRange}
          />
          <Button
            variant='secondary'
            size='sm'
            className='w-full sm:w-auto'
            disabled={isDownloading}
            onClick={() => {
              void handleDownloadCsv();
            }}
          >
            {isDownloading ? (
              <Loader2 className='animate-spin' />
            ) : (
              <Download />
            )}
            Download CSV
          </Button>
        </div>
      </div>

      {isError ? (
        <div className='rounded-xl border border-border bg-card p-6 text-center'>
          <p className='text-sm text-muted-foreground'>
            We could not load the attendance report. Please try again.
          </p>
          <Button
            variant='secondary'
            size='sm'
            className='mt-4'
            onClick={() => {
              void refetch();
            }}
          >
            Try again
          </Button>
        </div>
      ) : (
        <DataTable
          columns={attendanceReportColumns}
          data={rows}
          isUpdating={isFetching}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          getRowId={(row) => row.studentId}
          renderFooter={(table) => (
            <div className='mt-4'>
              <DataTablePagination table={table} />
            </div>
          )}
        />
      )}
    </div>
  );
}
